/**
 * Pure visibility evaluation, shared by field renderers, party/property
 * slot gating, and section numbering (per ADR-0049). Kept dependency-free
 * (no React) so atoms used in the server-side PDF render can import it
 * without pulling in the editor's field renderers.
 */
/**
 * Evaluate a VisibilityRule against the current values bag.
 * AND across keys; OR within a value-list.
 *
 * Matching modes (per `actual` shape):
 *   - `actual` is a scalar:
 *     * `expected` scalar → strict equality.
 *     * `expected` array  → `expected.includes(actual)` (OR).
 *   - `actual` is an array (multi_select-shaped value):
 *     * `expected` scalar → `actual.includes(expected)`.
 *     * `expected` array  → any-intersection (OR).
 */
export function evaluateVisibilityRule(rule, values) {
    if (!rule)
        return true;
    for (const [otherKey, expected] of Object.entries(rule)) {
        // Reserved `anyOf` OR-group: passes when at least one sub-rule matches.
        if (otherKey === 'anyOf') {
            const subRules = (Array.isArray(expected) ? expected : []);
            if (!subRules.some((r) => evaluateVisibilityRule(r, values)))
                return false;
            continue;
        }
        // Reserved `not` negation: passes when the sub-rule does NOT match.
        if (otherKey === 'not') {
            if (evaluateVisibilityRule(expected, values))
                return false;
            continue;
        }
        const actual = values[otherKey];
        if (Array.isArray(actual)) {
            const expectedArr = Array.isArray(expected) ? expected : [expected];
            if (!expectedArr.some((e) => actual.includes(e))) {
                return false;
            }
        }
        else if (Array.isArray(expected)) {
            if (!expected.includes(actual))
                return false;
        }
        else if (actual !== expected) {
            return false;
        }
    }
    return true;
}
/**
 * Compute whether a field should be rendered given current values.
 * AND across keys; OR within a value-list.
 */
export function isFieldVisible(field, values) {
    return evaluateVisibilityRule(field.visibleWhen, values);
}
//# sourceMappingURL=visibility.js.map